import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SharedAnimations } from '@stockcare-shared/animations/animations';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DoctorStorageService } from '@stockcare-storage/doctor/doctor-storage.service';
import { Doctor } from '@stockcare-storage/doctor/doctor';
import { SearchService } from '@stockcare-services/search-service/search.service';
import { MatDialogRef } from '@angular/material/dialog';
import { DoctorService } from '@stockcare-services/doctor/doctor.service';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { CustomKeyboardComponent } from '@stockcare-components/custom-keyboard/custom-keyboard.component';
import { KeyboardTemplate } from '@stockcare-components/custom-keyboard/keyboard-templates';

@Component({
  standalone: false,
  templateUrl: './theatre-doctor-list.component.html',
  styleUrls: ['./theatre-doctor-list.component.scss'],
  animations: [SharedAnimations],
})
export class TheatreDoctorListComponent implements OnInit, OnDestroy {
  @ViewChild('customKeyboard') public customKeyboard: CustomKeyboardComponent;
  public standardTemplate = KeyboardTemplate.StandardWithSpaceAndEnter;
  
  doctorList = {};
  offLineDoctors: Doctor[] = [];
  subscriptions = new Subscription();
  disableKeyPress = false;
  searchValue: string;
  delayInScanningTime: number;
  enableCustomKeyboard: boolean = false;
  isSearchFieldFocused: boolean = false;

  constructor(
    public readonly dialogRef: MatDialogRef<TheatreDoctorListComponent>,
    private readonly doctorStorage: DoctorStorageService,
    private readonly searchService: SearchService,
    private readonly doctorService: DoctorService,
    private readonly notify: NotificationService,
    private readonly settingService: SettingsService,
  ) {}

  async ngOnInit() {
    this.doctorService.ensureDoctorsLoaded().then(() => {
      this.getDoctors();
    });
    this.initializeSearch();
    this.enableCustomKeyboard = this.settingService.enableCustomKeyboard();
  }

  private getDoctors() {
    this.buildDoctors();
  }

  async buildDoctors() {
    this.offLineDoctors = await this.doctorStorage.getDoctorsByRange();
    this.sortAndGroupDoctors(this.offLineDoctors);
  }

  private sortAndGroupDoctors(doctors: Doctor[]) {
    this.doctorList = doctors;
    doctors.sort(this.compare);
  }

  private compare(doctor1: Doctor, doctor2: Doctor) {
    if (doctor1.Name > doctor2.Name) {
      return 1;
    } else {
      return -1;
    }
  }

  isEmptyDoctorList() {
    return this.doctorList && Object.keys(this.doctorList).length === 0;
  }

  initializeSearch() {
    this.subscriptions.add(
      this.searchService.scanned().subscribe((searchValue) => {
        this.searchValue = searchValue;
        this.search();
      })
    );

    this.subscriptions.add(
      this.searchService.typed().subscribe((searchValue) => {
        this.searchValue = searchValue;
        this.search();
      })
    );
  }

  search() {
    if (!this.offLineDoctors) {
      this.sortAndGroupDoctors([]);
      return;
    }

    if (!this.searchValue) {
      this.sortAndGroupDoctors(this.offLineDoctors);
      return;
    }

    const doctors = this.doctorService.doctors.filter(
      (x) =>
        x.Name.toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1 ||
        x.PracticeNumber.toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1 ||
        x.ReferenceId.toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1
    );

    if (!doctors || doctors.length === 0) {
      this.notify.error('No matching doctor was found', 'Doctor not found');
      return;
    }

    this.sortAndGroupDoctors(doctors);
  }

  selectDoctor(doctor: Doctor) {
    this.dialogRef.close(doctor);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSearchHeaderInputFocused(value: boolean) {
    this.isSearchFieldFocused = value;
  }

  isCustomKeyboardVisible(): boolean {
    return this.enableCustomKeyboard && this.isSearchFieldFocused;
  }

  public customKeyboardInput(inputText: string) {
    this.searchValue = inputText;
    this.search();
  }
}
